import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { PURPOSE_KINDS, PurposeChips, TagChips,
         purposeCountKey } from '../../components/common/EventTags';
import CompanyEventForm, { NewEventButton, emptyEventDraft,
                           eventCreateBody } from '../../components/common/CompanyEventForm';
import CompanyEventRow from './CompanyEventRow';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import { ManagingBar } from './ClubPage';
import { LockedCreateButton } from '../../components/common/ModuleInterest';

/* One programme and everything under it — where a customer creates the events that hang under
   it.

   ★ The create form itself is ``components/common/CompanyEventForm``, shared with the league
   screen. A league's master and a programme are the same shape — a container a day hangs under
   via ``Event.master_event_id`` — so they post the same body to the same route, and keeping two
   copies of a form with a repeat-count rule and an end-date-drag rule in it was a bug waiting to
   be written twice. */

/* Where an event is, as one comparable key. Online wins over a venue name because that is the
   order the row itself reads them in (``CompanyEventRow``) — an event flagged online prints
   "Online", so it must filter as Online too. Folded to lower case so one venue typed two ways is
   one place; empty when the event says nothing about where it is. */
const ONLINE = '__online__';
const placeKey = (e) => (e.is_online ? ONLINE : (e.location_name || '').trim().toLowerCase());

/* What an event IS, beyond the categories somebody typed: whose event it is, and whether the day
   is a competition. Two facts the company never tags because the event already knows them — one
   off ``is_external_event``, one off having a tournament stage — and both worth a chip, because
   "the ones we are only going to" and "the ones with a bracket" are the two questions a long
   programme gets asked that no category answers.

   A LIST rather than one key: somebody else's competition is both, and has to answer to either
   chip. Internal is the absence of external rather than a flag of its own, so an event that
   predates the whole idea sorts as ours, which it is. */
const natureKeys = (e) => (e.is_external_event ? ['external'] : ['internal'])
  .concat(e.is_competition ? ['competition'] : []);

/* A name as the search box compares it: lower case, accents off, so "cafe" finds "Café". */
const fold = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/* Which days an event covers, as the 'YYYY-MM-DD' an <input type=date> speaks. The payload's
   dates are already event-LOCAL wall clock, so the day is the first ten characters — no Date
   object, which would shift it by the browser's offset. An event with no end lasts its one day. */
const dayOf = (s) => (s ? s.slice(0, 10) : '');

const CompanyProgramme = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // Which categories the list below is narrowed to. Filtered in the browser, not re-fetched:
  // a programme holds a few dozen events at most and they are all already here, so a round
  // trip per chip would only make the chips feel slow.
  const [filter, setFilter] = useState([]);
  // And what the events are FOR — themes, values, goals — as a second, independent sieve.
  const [purposeFilter, setPurposeFilter] = useState({ theme: [], value: [], sdg: [] });
  // And WHERE they are. A third sieve of the same kind, except that there is no catalogue of
  // venues to draw it from — the chips are whatever the events under this programme say.
  const [placeFilter, setPlaceFilter] = useState([]);
  // And WHAT KIND of thing each one is — ours or somebody else's, and whether it is a
  // competition. See ``natureKeys``.
  const [natureFilter, setNatureFilter] = useState([]);
  // ★ The BASIC filters — what a long programme is searched by most: a name somebody remembers,
  // a stretch of dates, and who is hosting. Always on screen. The chip rows above are the extra
  // ones, behind "More filters" (``more``), because five rows of chips above the list pushed the
  // events themselves off the first screen.
  const [query, setQuery] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [hostFilter, setHostFilter] = useState('');
  const [more, setMore] = useState(false);

  // No id in the URL is a legitimate way in (the home tile): the server answers with the
  // programme the company is in right now, and tells us which one that turned out to be.
  const load = useCallback(() => {
    apiRequest('GET', programmeId ? `/company/programme/${programmeId}` : '/company/programme')
      .then(setData)
      // Nothing to show — no programmes yet, or an id that is not this company's.
      .catch(() => navigate('/programmes'));
  }, [programmeId, navigate]);

  useEffect(() => { load(); }, [load]);

  // ★ Which programme a new event under this screen belongs to. The URL param is right for the
  // GET and wrong for the create: on /programme there is no id to read, so the event posted no
  // programme at all ("pick the programme this event belongs to"). The server has already said
  // which programme it picked — that is the id the create belongs to.
  const currentId = (data && data.programme && data.programme.id) || programmeId;

  const create = () => {
    if (busy || !draft || !draft.name.trim() || !draft.date) return;
    setBusy(true);
    setNotice(null);
    const body = eventCreateBody(draft, currentId,
                                 { timeZone: data && data.programme && data.programme.time_zone });
    apiRequest('POST', '/company/events', body)
      .then((res) => {
        setDraft(null);
        // ★ Straight into the new event's editor rather than back to this list. The create
        // form asks the three things an event cannot exist without; where it is, what it is
        // for and whether anybody may see it are asked in the editor, and they are asked NOW
        // — on a programme holding a dozen rows, a new one appearing somewhere in the middle
        // of them is not an answer to "what happens next". A series says how many it made on
        // the way in, since this screen is no longer here to print it.
        const newId = res.event && res.event.id;
        if (newId) {
          navigate(`/company-event/${newId}/edit`, res.series_created
            ? { state: { notice: t('seriesCreated', { n: res.series_created + 1 }) } }
            : undefined);
          return;
        }
        if (res.series_created) setNotice(t('seriesCreated', { n: res.series_created + 1 }));
        load();
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const programme = data && data.programme;
  const events = (data && data.events) || [];
  const tags = (data && data.tags) || [];
  const programmes = (data && data.programmes) || [];
  // How many events under THIS programme carry each category — counted over the whole list, not
  // the filtered one, so the numbers stay put while you click the chips.
  const tagCounts = {};
  events.forEach((e) => (e.tags || []).forEach((tg) => {
    const k = String(tg.id).toLowerCase();
    tagCounts[k] = (tagCounts[k] || 0) + 1;
  }));
  // The same tally for what the events are for. Keyed on kind AND id, because all three kinds
  // share one map and a theme's id says nothing about which row it belongs to.
  const purposeCounts = {};
  events.forEach((e) => (e.purpose || []).forEach((it) => {
    const k = purposeCountKey(it.kind, it.id);
    purposeCounts[k] = (purposeCounts[k] || 0) + 1;
  }));
  // And where they are. Online is a place like any other here — it is what the row itself
  // prints instead of a venue, so it has to be filterable the same way. Two spellings of one
  // venue ("HQ Brussels", "HQ brussels") are ONE chip: the key is folded, the label is the
  // first spelling seen. An event with neither a venue nor the online flag gets no key, so it
  // offers no chip and drops out the moment this row is used.
  const placeCounts = {};
  const placeNames = {};
  events.forEach((e) => {
    const k = placeKey(e);
    if (!k) return;
    placeCounts[k] = (placeCounts[k] || 0) + 1;
    if (!placeNames[k]) {
      placeNames[k] = k === ONLINE ? t('onlineEvent') : e.location_name.trim();
    }
  });
  // Busiest venue first — a filter is read to find where most of it happens — then by name so
  // the order cannot wobble between two places used equally often.
  const places = Object.keys(placeCounts).sort((a, b) => (
    placeCounts[b] - placeCounts[a] || placeNames[a].localeCompare(placeNames[b])
  )).map((k) => ({ id: k, name: placeNames[k] }));

  // The same tally for what kind of thing each event is, and then only the chips that would
  // narrow anything. Ours/theirs needs BOTH to exist — a programme where everything is ours
  // would get a chip that hides nothing when it is on, which is the one chip that makes a filter
  // row read as broken — and the competition chip needs a competition.
  const natureCounts = { internal: 0, external: 0, competition: 0 };
  events.forEach((e) => natureKeys(e).forEach((k) => { natureCounts[k] += 1; }));
  const natureChips = [];
  if (natureCounts.internal > 0 && natureCounts.external > 0) {
    natureChips.push({ id: 'internal', name: t('eventInternal') },
                     { id: 'external', name: t('eventExternal') });
  }
  if (natureCounts.competition > 0) {
    natureChips.push({ id: 'competition', name: t('tournamentTitle') });
  }

  // Who hosts what, for the host picker: everybody named on at least one event here, busiest
  // first — the same order the venues use, for the same reason.
  const hostCounts = {};
  const hostNames = {};
  events.forEach((e) => (e.hosts || []).forEach((h) => {
    hostCounts[h.id] = (hostCounts[h.id] || 0) + 1;
    if (!hostNames[h.id]) hostNames[h.id] = h.name;
  }));
  const hosts = Object.keys(hostCounts).sort((a, b) => (
    hostCounts[b] - hostCounts[a] || hostNames[a].localeCompare(hostNames[b])
  ));

  // ANY of the picked categories, not all — a chip row reads as "show me these kinds". ACROSS
  // rows it is the other way round: picking a category and a theme means events that are both,
  // because a filter that got LONGER as you added to it would answer nobody's question.
  const needle = fold(query.trim());
  const matches = (e) => {
    if (needle && !fold(e.name).includes(needle)) return false;
    // A date range keeps every event that TOUCHES it, so a three-day offsite that starts the day
    // before "from" is still in. An event with no date at all cannot be placed, and drops out.
    if (fromDay || toDay) {
      const start = dayOf(e.date);
      const end = dayOf(e.end_date) || start;
      if (!start) return false;
      if (fromDay && end < fromDay) return false;
      if (toDay && start > toDay) return false;
    }
    if (hostFilter && !(e.hosts || []).some((h) => h.id === hostFilter)) return false;
    if (filter.length > 0 && !(e.tags || []).some((tg) => filter.includes(tg.id))) return false;
    if (natureFilter.length > 0
        && !natureKeys(e).some((k) => natureFilter.includes(k))) return false;
    if (placeFilter.length > 0 && !placeFilter.includes(placeKey(e))) return false;
    const has = new Set((e.purpose || []).map((it) => purposeCountKey(it.kind, it.id)));
    return PURPOSE_KINDS.every((kind) => {
      const picked = purposeFilter[kind] || [];
      return picked.length === 0
        || picked.some((id) => has.has(purposeCountKey(kind, id)));
    });
  };
  const shown = events.filter(matches);
  // How many of the EXTRA filters are on — the number on the "More filters" line, so a filter
  // hidden in the closed drawer can never be the silent reason the list is short. Any of them on
  // also keeps the drawer open, the same rule the community wall follows.
  const extraOn = filter.length + placeFilter.length + natureFilter.length
    + PURPOSE_KINDS.reduce((n, kind) => n + (purposeFilter[kind] || []).length, 0);
  const drawerOpen = more || extraOn > 0;
  const filtering = extraOn > 0 || !!needle || !!fromDay || !!toDay || !!hostFilter;
  const toggleFilter = (id) => setFilter((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const toggleNatureFilter = (id) => setNatureFilter((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const togglePlaceFilter = (id) => setPlaceFilter((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const togglePurposeFilter = (kind, id) => setPurposeFilter((p) => {
    const on = p[kind] || [];
    return { ...p, [kind]: on.includes(id) ? on.filter((x) => x !== id) : [...on, id] };
  });
  const clearFilters = () => {
    setFilter([]);
    setPlaceFilter([]);
    setNatureFilter([]);
    setPurposeFilter({ theme: [], value: [], sdg: [] });
    setQuery('');
    setFromDay('');
    setToDay('');
    setHostFilter('');
  };

  // ★ The create form lives at the BOTTOM of the card, under the rows, and the + that opens it
  // sits at the top — on a programme with a dozen events those are two screens apart, and a
  // button that scrolls nothing reads as a button that did nothing. Keyed on whether the draft
  // exists rather than on the draft itself, or every keystroke in the form would drag the page.
  const formRef = useRef(null);
  const drafting = !!draft;
  useEffect(() => {
    if (drafting && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [drafting]);
  const openDraft = () => setDraft(emptyEventDraft());

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/programmes" />
        {data && data.club_context && (
          <div className="mt-3">
            <ManagingBar clubId={data.club_context.id} name={data.club_context.name} />
          </div>
        )}
        <Title my="1" className="mt-3">
          <Icon name="calendar-star" theme="primary" className="me-2" />
          {programme ? programme.name : t('programmes')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            {/* When it runs. ★ Changing it is NOT done here: a programme is edited from the
                list it sits in (/programmes), beside the row that names it, so this screen is
                only ever about what hangs UNDER the programme. */}
            <p className="bravos-q__hint">
              {[shortDate(programme.date, i18n.language),
                shortDate(programme.end_date, i18n.language)]
                .filter(Boolean).join(' – ') || t('themeNoWindow')}
            </p>
            {/* ★ Only when it SAYS something. A description that is the programme's own name
                back again printed the title a third time, right under the date. */}
            {programme.short_desc
              && programme.short_desc.trim().toLowerCase()
                 !== (programme.name || '').trim().toLowerCase()
              && <p className="ce-note">{programme.short_desc}</p>}
          </>
        )}
        {notice && !draft && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {data && (
        <Card>
          {/* The count, and at the right-hand end of that same line the way to add one more.
              A + rather than a labelled button: it repeats what the one under the list already
              says in words, and is here only so that adding an event does not first mean
              scrolling past everything already in the programme. Gone while the form is open —
              it is the form's own way in, and there is nothing further to open. */}
          <div className="ce-head">
            <Title my="1" className="mt-2">
              <Icon name="calendar" theme="primary" className="me-2" />
              {t('eventsInProgramme', { n: shown.length })}
            </Title>
            {!draft && (
              <button
                type="button"
                className="ce-head__add"
                onClick={openDraft}
                title={t('newEvent')}
                aria-label={t('newEvent')}
              >
                <Icon name="plus" />
              </button>
            )}
          </div>

          {/* The filters, right under the heading and above the rows they narrow: WHICH
              programme, then the BASIC three (name, dates, host), then behind "More filters"
              what CATEGORY of event it is, what KIND of thing it is, WHERE it is and what it is
              FOR.

              ★ The programme sits here rather than over the title because that is what it does
              — it is the widest sieve of them all, and reading it beside the others says so.
              Only drawn when there IS somewhere else to go, and a real navigation rather than
              local state, so the URL keeps naming what you are looking at and the back button
              behaves. The chips below it need more than one event to be worth drawing at all,
              and the venues need more than one venue: a programme that happens in a single room
              would get one chip that hides nothing when it is on. */}
          {programme && programmes.length > 1 && (
            <>
              <span className="etags__label">{t('programme')}</span>
              <select
                className="ce-progpick"
                value={programme.id}
                onChange={(e) => navigate(`/programme/${e.target.value}`)}
              >
                {programmes.map((pr) => (
                  <option key={pr.id} value={pr.id}>
                    {pr.name}
                    {pr.date ? ` — ${shortDate(pr.date, i18n.language)}` : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          {events.length > 1 && (
            <>
              {/* The basic three, on one line that wraps. The host picker only when somebody is
                  named on at least one event — an "Any host" select with nothing under it is a
                  control that cannot do anything. */}
              <div className="ce-basic">
                <label className="ce-basic__field ce-basic__field--wide">
                  <span className="etags__label">{t('search')}</span>
                  <input
                    type="search"
                    className="ce-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('searchEventName')}
                  />
                </label>
                <label className="ce-basic__field">
                  <span className="etags__label">{t('dateFrom')}</span>
                  <input type="date" className="ce-input" value={fromDay} max={toDay || undefined}
                         onChange={(e) => setFromDay(e.target.value)} />
                </label>
                <label className="ce-basic__field">
                  <span className="etags__label">{t('dateTo')}</span>
                  <input type="date" className="ce-input" value={toDay} min={fromDay || undefined}
                         onChange={(e) => setToDay(e.target.value)} />
                </label>
                {hosts.length > 0 && (
                  <label className="ce-basic__field">
                    <span className="etags__label">{t('hostedBy')}</span>
                    <select className="ce-input" value={hostFilter}
                            onChange={(e) => setHostFilter(e.target.value)}>
                      <option value="">{t('anyHost')}</option>
                      {hosts.map((id) => (
                        <option key={id} value={id}>{hostNames[id]} ({hostCounts[id]})</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              <button type="button" className="ce-drawer" onClick={() => setMore(!more)}
                      aria-expanded={drawerOpen}>
                <Icon name={drawerOpen ? 'angle-small-up' : 'angle-small-down'} className="me-1" />
                {t('moreFilters')}
                {extraOn > 0 && <span className="ce-drawer__n">{extraOn}</span>}
              </button>

              {drawerOpen && (
                <div className="ce-drawer__body">
                  <TagChips tags={tags} selected={filter} onToggle={toggleFilter}
                            counts={tagCounts} label={t('eventTags')} small />
                  {/* Ours / theirs / a competition. One row rather than two, because the rule
                      this screen already holds to is ANY within a row and ALL across them — so
                      these three read as "show me these kinds", the same sentence the categories
                      above them read as, and asking for the external ones AND the competitions
                      is a question you put to the two rows, not to this one. Draws nothing when
                      none of the three would narrow the list. */}
                  <TagChips tags={natureChips} selected={natureFilter}
                            onToggle={toggleNatureFilter} counts={natureCounts}
                            label={t('eventKind')} small />
                  {places.length > 1 && (
                    <TagChips tags={places} selected={placeFilter} onToggle={togglePlaceFilter}
                              counts={placeCounts} label={t('location')} small />
                  )}
                  <PurposeChips catalog={data.purpose_catalog} picks={purposeFilter}
                                onToggle={togglePurposeFilter} counts={purposeCounts} />
                </div>
              )}
              {filtering && (
                <button type="button" className="bravos-tag__add mt-2" onClick={clearFilters}>
                  {t('clearFilter')}
                </button>
              )}
            </>
          )}

          {events.length === 0 && <p className="ce-note">{t('noEventsYet')}</p>}
          {events.length > 0 && shown.length === 0 && (
            <p className="ce-note">{t('noEventsForFilter')}</p>
          )}

          {shown.map((e) => (
            <CompanyEventRow
              key={e.id}
              event={e}
              lang={i18n.language}
              onOpen={() => navigate(`/company-event/${e.id}/edit`)}
              actions={(
                <button
                  type="button"
                  className="ce-person__act"
                  onClick={(ev) => { ev.stopPropagation();
                                     navigate(`/company-event/${e.id}/people`); }}
                >
                  {e.rsvp ? e.rsvp.going : 0} <Icon name="users" />
                </button>
              )}
            />
          ))}

          <div ref={formRef}>
            {draft ? (
              <CompanyEventForm
                draft={draft}
                setDraft={setDraft}
                tags={tags}
                purposeCatalog={data.purpose_catalog}
                busy={busy}
                onSave={create}
                onCancel={() => setDraft(null)}
              />
            ) : (data && data.can_create === false ? (
              /* Seeing the events is free, making them is the paid "Own events" module. */
              <LockedCreateButton label={t('newEvent')} />
            ) : (
              <NewEventButton label={t('newEvent')} onClick={openDraft} />
            ))}
          </div>
          {notice && <p className="bravos-cal__notice">{notice}</p>}
        </Card>
      )}
    </Base>
  );
};

export default CompanyProgramme;
